import httpClient from '../utils/httpClient';

const API_URL = '/payroll';

const normalizePayrollPeriodRow = (p) => ({
  ...p,
  id: Number(p.id),
  month: p.month != null ? Number(p.month) : p.month,
  year: p.year != null ? Number(p.year) : p.year,
  enabled: p.enabled != null ? Number(p.enabled) : p.enabled,
});

/**
 * Crear período de liquidación
 * @param {Object} data - { month, year, description }
 * @returns {Promise}
 */
export const createPayrollPeriod = async (data) => {
  const response = await httpClient.post(`${API_URL}/period`, data);
  return response;
};

/**
 * Obtener todos los períodos
 * @returns {Promise}
 */
export const getAllPayrollPeriods = async () => {
  const response = await httpClient.get(`${API_URL}/periods`);
  const list = response?.payload?.periods;
  if (Array.isArray(list)) {
    response.payload.periods = list
      .map(normalizePayrollPeriodRow)
      .filter((row) => Number.isFinite(row.id) && row.id >= 1);
  }
  return response;
};

/**
 * Obtener período por ID
 * @param {number} periodId
 * @returns {Promise}
 */
export const getPayrollPeriodById = async (periodId) => {
  const response = await httpClient.get(`${API_URL}/periods/${periodId}`);
  return response;
};

/**
 * Calcular liquidación masiva para un conjunto de empleados
 * @param {Object} data - { periodId, employeeIds }
 * @param {boolean} overwrite - Si sobreescribe liquidaciones existentes
 * @returns {Promise}
 */
export const calculatePayroll = async (data, overwrite = false) => {
  const url = overwrite ? `${API_URL}/calculate-batch?overwrite=true` : `${API_URL}/calculate-batch`;
  const response = await httpClient.post(url, data);
  return response;
};

/**
 * Obtener liquidaciones por período
 * @param {number} periodId
 * @returns {Promise}
 */
export const getPayrollsByPeriod = async (periodId) => {
  const response = await httpClient.get(`${API_URL}/period/${periodId}/payrolls`);
  return response;
};

/**
 * Obtener liquidación por ID
 * @param {number} payrollId
 * @returns {Promise}
 */
export const getPayrollById = async (payrollId) => {
  const response = await httpClient.get(`${API_URL}/${payrollId}`);
  return response;
};

/**
 * Obtener detalle de conceptos de una liquidación
 * @param {number} payrollId
 * @returns {Promise}
 */
export const getPayrollDetails = async (payrollId) => {
  const response = await httpClient.get(`${API_URL}/details/${payrollId}`);
  return response;
};

/**
 * Actualizar estado de liquidación
 * @param {number} payrollId
 * @param {Object} data - { status }
 * @returns {Promise}
 */
export const updatePayrollStatus = async (payrollId, data) => {
  const response = await httpClient.put(`${API_URL}/${payrollId}/status`, data);
  return response;
};

/**
 * Eliminar liquidación
 * @param {number} payrollId
 * @returns {Promise}
 */
export const deletePayroll = async (payrollId) => {
  const response = await httpClient.delete(`${API_URL}/${payrollId}`);
  return response;
};

/**
 * Obtener configuración IRPF
 * @param {number} year
 * @returns {Promise}
 */
export const getPayrollConfig = async (year) => {
  const response = await httpClient.get(`${API_URL}/config/${year}`);
  return response;
};

/**
 * Crear/actualizar configuración IRPF
 * @param {Object} data - { year, bpc, brackets }
 * @returns {Promise}
 */
export const savePayrollConfig = async (data) => {
  const response = await httpClient.post(`${API_URL}/config`, data);
  return response;
};

/**
 * Obtener historial de liquidaciones por empleado
 * @param {number} employeeId
 * @returns {Promise}
 */
export const getEmployeePayrollHistory = async (employeeId) => {
  const response = await httpClient.get(`${API_URL}/employee/${employeeId}/history`);
  return response;
};

/**
 * Obtener liquidaciones con filtros
 * @param {Object} filters - { status?, employeeId?, year?, month?, periodId? }
 * @returns {Promise}
 */
export const getPayrollsByFilters = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.status) params.append('status', filters.status);
  if (filters.employeeId) params.append('employeeId', filters.employeeId);
  if (filters.year) params.append('year', filters.year);
  if (filters.month) params.append('month', filters.month);
  if (filters.periodId) params.append('periodId', filters.periodId);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.search) params.append('search', filters.search);
  
  const queryString = params.toString();
  const url = queryString ? `${API_URL}/all?${queryString}` : `${API_URL}/all`;
  
  const response = await httpClient.get(url);
  return response;
};

export const downloadPayrollPdf = async (payrollId) => {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const response = await fetch(`${baseUrl}/payroll/receipt/${payrollId}/pdf`, {
    headers: { Authorization: token ? `Bearer ${token}` : '' },
  });
  if (!response.ok) throw new Error('Error al descargar PDF');
  return response.blob();
};

/**
 * PDF de recibo de liquidación final (despido / renuncia) generado desde el body.
 * Debe coincidir con SettlementReceiptPdfDto en el backend.
 * @param {Object} body
 * @returns {Promise<Blob>}
 */
export const downloadSettlementReceiptPdf = async (body) => {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const response = await fetch(`${baseUrl}/payroll/receipt/settlement-pdf`, {
    method: 'POST',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let msg = 'Error al descargar PDF de liquidación final';
    try {
      const j = await response.json();
      if (j?.message) msg = Array.isArray(j.message) ? j.message.join(', ') : j.message;
    } catch {
      /* ignorar */
    }
    throw new Error(msg);
  }
  return response.blob();
};

export const downloadMonthlyReport = async (year, month) => {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const response = await fetch(
    `${baseUrl}/payroll/report/monthly?year=${year}&month=${month}`,
    { headers: { Authorization: token ? `Bearer ${token}` : '' } },
  );
  if (!response.ok) throw new Error('Error al descargar reporte');
  return response.blob();
};
